import React, { useState, useEffect, useRef } from 'react';
import { ChevronRight } from 'lucide-react';

// Color constants
const gold = "#f5a623";
const blue = "#3b82f6";
const blue2 = "#60a5fa";
const green = "#22c55e";
const red = "#ef4444";
const navy3 = "#1a2236";
const navy4 = "#212d45";
const white = "#ffffff";
const textCol = "#cbd5e1";
const muted = "#64748b";
const border = "rgba(255,255,255,0.07)";

// Shared style atoms
const s = {
  section: { marginBottom: 48 },
  eyebrow: { display: "flex", alignItems: "center", gap: 12, marginBottom: 12 },
  tag: { fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.16em", textTransform: "uppercase", color: blue },
  badge: { fontFamily: "monospace", fontSize: "0.68rem", color: muted },
  h2: { fontFamily: "Georgia,serif", fontSize: "1.75rem", fontWeight: 800, color: white, lineHeight: 1.3, marginBottom: 16 },
  prose: { fontSize: "0.95rem", color: textCol, lineHeight: 1.8, marginBottom: 20 },
  hr: { border: "none", borderTop: `1px solid ${border}`, margin: "40px 0" },
  pillGold: { display: "inline-block", background: "rgba(245,166,35,.12)", border: "1px solid rgba(245,166,35,.2)", color: gold, fontSize: "0.75rem", fontWeight: 700, padding: "3px 10px", borderRadius: 4, marginRight: 4, marginBottom: 4 },
};

function Callout({ icon, children, accent = gold }) {
  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderLeft: `3px solid ${accent}`, borderRadius: 8, padding: "20px 24px", margin: "24px 0", display: "flex", gap: 14, alignItems: "flex-start" }}>
      <span style={{ fontSize: "1.1rem", flexShrink: 0, marginTop: 2 }}>{icon}</span>
      <div style={{ fontSize: "0.88rem", color: textCol, lineHeight: 1.75 }}>{children}</div>
    </div>
  );
}

function ColCard({ label, labelColor = gold, title, body, accent = gold }) {
  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 24, position: "relative", overflow: "hidden" }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg,${accent},transparent)` }} />
      <div style={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: labelColor, marginBottom: 6 }}>{label}</div>
      <div style={{ fontFamily: "Georgia,serif", fontSize: "1.1rem", fontWeight: 800, color: white, marginBottom: 12 }}>{title}</div>
      {body && <div style={{ fontSize: "0.85rem", color: muted, lineHeight: 1.7 }}>{body}</div>}
    </div>
  );
}

// Section 1: 6 Steps
const steps = [
  { num: "01", icon: "🤝", title: "Establish the Relationship", body: "Define the terms of engagement, compensation, and each party's responsibilities. This sets expectations and creates a professional framework before any financial advice is given." },
  { num: "02", icon: "📋", title: "Collect Data & Information", body: 'Gather both "hard" KYC facts (age, income, net worth) and "soft" facts (fears, preferences, values, goals). Incomplete data means incomplete recommendations.' },
  { num: "03", icon: "🔍", title: "Analyze Data & Information", body: "Assess the client's financial status — net worth, cash flow, existing holdings — and identify constraints: tax situation, liquidity needs, time horizons." },
  { num: "04", icon: "💡", title: "Recommend Strategies", body: "Develop a plan to meet the client's goals — specific asset allocation targets, fund selections, account types (RRSP/TFSA). Recommendations must be justified and documented." },
  { num: "05", icon: "⚡", title: "Implement Recommendations", body: "Execute the trades, set up the accounts, and refer to specialists (lawyers, accountants) where needed. The plan only has value once it's actually enacted." },
  { num: "06", icon: "🔄", title: "Conduct Periodic Review", body: "Review at a minimum every 36 months under CFRs, or immediately when major life events occur: marriage, divorce, job loss, inheritance, or a significant change in financial circumstances." },
];

function SixSteps() {
  const [active, setActive] = useState(0);
  return (
    <div style={{ margin: "24px 0" }}>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 0 }}>
        {steps.map((st, i) => (
          <button key={i} onClick={() => setActive(i)} style={{
            background: active === i ? navy4 : navy3, border: `1px solid ${active === i ? "rgba(245,166,35,.35)" : border}`,
            borderBottom: active === i ? `2px solid ${gold}` : `1px solid ${border}`,
            borderRadius: "6px 6px 0 0", padding: "10px 14px", cursor: "pointer",
            fontFamily: "inherit", fontSize: "0.78rem", fontWeight: 700,
            color: active === i ? gold : muted, transition: "all 0.2s",
          }}>
            <span style={{ fontFamily: "monospace", fontSize: "0.65rem", color: muted, marginRight: 6 }}>{st.num}</span>{st.icon}
          </button>
        ))}
      </div>
      <div style={{ background: navy4, border: `1px solid rgba(245,166,35,.15)`, borderRadius: "0 0 8px 8px", padding: 28 }}>
        <div style={{ fontFamily: "Georgia,serif", fontSize: "1.15rem", color: white, marginBottom: 10 }}>
          {steps[active].icon} Step {steps[active].num} — {steps[active].title}
        </div>
        <p style={{ fontSize: "0.9rem", color: textCol, lineHeight: 1.8 }}>{steps[active].body}</p>
      </div>
    </div>
  );
}

// KYC Components
const kycItems = [
  { id: "A", label: "Personal Data", icon: "👤", body: "Age, marital status, dependents, employment, and health. These factors directly determine the client's time horizon, liquidity needs, and overall financial situation.", pills: ["Age & Life Stage", "Dependents", "Employment", "Health"] },
  { id: "B", label: "Financial Circumstances", icon: "💰", body: "Net Worth (Assets − Liabilities) and Cash Flow (Income − Expenses). The emergency fund benchmark is 3–6 months of living expenses in liquid assets before aggressive investing begins.", pills: ["Net Worth", "Cash Flow", "Emergency Fund (3–6 months)", "Budget Analysis"] },
  { id: "C", label: "Investment Objectives", icon: "🎯", body: "What does the client want the money to do? Safety of Capital (Money Market), Current Income (Bond/Mortgage Funds), Capital Growth (Equity Funds), or Tax Minimization (Dividends/Capital Gains).", pills: ["Safety of Capital", "Current Income", "Capital Growth", "Tax Minimization"] },
  { id: "D", label: "Investment Knowledge", icon: "📚", body: "Does the client understand the risk/return trade-off? A sophisticated client may accept higher volatility; a novice needs education and likely lower-risk products until their knowledge improves.", pills: ["Novice", "Fair", "Good", "Sophisticated"] },
  { id: "E", label: "Risk Profile", icon: "⚠️", body: "The LOWER of risk tolerance (psychological willingness) and risk capacity (financial ability to absorb loss). A wealthy senior who is terrified of losing money still has a LOW risk profile.", pills: ["Risk Tolerance (psychological)", "Risk Capacity (financial)", "Use the LOWER of the two"] },
  { id: "F", label: "Time Horizon", icon: "⏳", body: "Short-term (<3 years) requires liquidity and safety — Money Market Funds. Long-term (10+ years) can tolerate volatility in pursuit of higher growth through Equity Funds.", pills: ["Short-term <3 yrs → Safety", "Medium 3–10 yrs → Balance", "Long-term 10+ yrs → Growth"] },
];

function KYCAccordion() {
  const [open, setOpen] = useState("A");
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(280px,1fr))", gap: 10, margin: "24px 0" }}>
      {kycItems.map(item => (
        <div key={item.id} onClick={() => setOpen(open === item.id ? null : item.id)}
          style={{ background: navy3, border: `1px solid ${open === item.id ? "rgba(245,166,35,.35)" : border}`, borderRadius: 8, overflow: "hidden", cursor: "pointer", transition: "border-color 0.2s" }}>
          <div style={{ padding: "14px 18px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ background: "rgba(245,166,35,.12)", border: "1px solid rgba(245,166,35,.2)", borderRadius: 4, width: 28, height: 28, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "monospace", fontSize: "0.7rem", color: gold, flexShrink: 0 }}>{item.id}</div>
              <div style={{ fontSize: "0.88rem", fontWeight: 700, color: white }}>{item.icon} {item.label}</div>
            </div>
            <span style={{ color: open === item.id ? gold : muted, transform: open === item.id ? "rotate(90deg)" : "none", transition: "transform 0.25s", fontSize: "0.9rem" }}>›</span>
          </div>
          {open === item.id && (
            <div style={{ padding: "0 18px 16px", borderTop: `1px solid ${border}` }}>
              <p style={{ fontSize: "0.84rem", color: muted, lineHeight: 1.7, margin: "12px 0 10px" }}>{item.body}</p>
              <div>{item.pills.map((p, i) => <span key={i} style={s.pillGold}>{p}</span>)}</div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// Life Cycle
const lifecycle = [
  { stage: 1, label: "Early Earning", age: "To age 30", icon: "🌱", color: green, chars: ["Young, single, few assets", "Short-term goals (car, vacation)", "Long time horizon available", "High risk tolerance"], alloc: "High Equity for long-term OR Cash for near-term purchases", allocColor: green },
  { stage: 2, label: "Family Commitment", age: "25 – 35", icon: "👨‍👩‍👧", color: blue, chars: ["Married with children", "Mortgage and high expenses", "Lack of liquidity is the key feature", "Hard to save significantly"], alloc: "Conservative/Balanced — lower risk due to dependents and debt obligations", allocColor: blue },
  { stage: 3, label: "Mature Earning", age: "30 – 50", icon: "📈", color: gold, chars: ["Higher income, stabilizing expenses", "Wealth accumulation years", "Tax minimization becomes important", "Focus on retirement planning"], alloc: "Shift back to Equities/Growth — long horizon for retirement, tax-efficient focus", allocColor: gold },
  { stage: 4, label: "Nearing Retirement", age: "45 – 65", icon: "🎯", color: "#f59e0b", chars: ['"Empty nesters" — children have left', "Peak earning years", "Aggressive savings for retirement", "Realization that time is running out"], alloc: "Growth but progressively reducing risk. Strong focus on tax efficiency and diversification", allocColor: "#f59e0b" },
  { stage: 5, label: "Retired", age: "50+", icon: "🏖️", color: "#a78bfa", chars: ["Relying on savings for income", "Determining estate/wealth transfer goals", "Capital preservation is paramount", "Regular income needs from portfolio"], alloc: "Income/Security focus — Fixed Income and Dividend-paying Equities", allocColor: "#a78bfa" },
];

function LifeCycle() {
  const [active, setActive] = useState(0);
  const lc = lifecycle[active];
  return (
    <div style={{ margin: "24px 0" }}>
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        {lifecycle.map((l, i) => (
          <button key={i} onClick={() => setActive(i)} style={{
            flex: 1, minWidth: 80, background: active === i ? navy4 : navy3,
            border: `1px solid ${active === i ? l.color + "55" : border}`,
            borderRadius: 8, padding: "12px 8px", cursor: "pointer", transition: "all 0.2s",
            textAlign: "center", fontFamily: "inherit",
          }}>
            <div style={{ fontSize: "1.2rem", marginBottom: 4 }}>{l.icon}</div>
            <div style={{ fontSize: "0.7rem", fontWeight: 700, color: active === i ? l.color : muted, letterSpacing: "0.06em" }}>Stage {l.stage}</div>
            <div style={{ fontSize: "0.72rem", color: active === i ? white : muted, marginTop: 2 }}>{l.label}</div>
          </button>
        ))}
      </div>
      <div style={{ background: navy4, border: `1px solid ${lc.color}33`, borderRadius: 8, padding: 28 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <span style={{ fontSize: "1.8rem" }}>{lc.icon}</span>
          <div>
            <div style={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: lc.color, marginBottom: 3 }}>Stage {lc.stage} · {lc.age}</div>
            <div style={{ fontFamily: "Georgia,serif", fontSize: "1.25rem", color: white }}>{lc.label} Years</div>
          </div>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <div style={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: muted, marginBottom: 8 }}>Characteristics</div>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {lc.chars.map((c, i) => (
                <li key={i} style={{ fontSize: "0.85rem", color: textCol, padding: "5px 0", borderBottom: `1px solid ${border}`, display: "flex", gap: 8 }}>
                  <span style={{ color: lc.color, flexShrink: 0 }}>→</span>{c}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div style={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: muted, marginBottom: 8 }}>Asset Allocation Focus</div>
            <div style={{ background: navy3, border: `1px solid ${lc.allocColor}33`, borderRadius: 6, padding: 16 }}>
              <div style={{ fontSize: "0.85rem", color: textCol, lineHeight: 1.7 }}>{lc.alloc}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Pyramid
function Pyramid() {
  const [hovered, setHovered] = useState(null);
  const layers = [
    { id: "top", label: "Speculation", sub: "Only with surplus funds", color: red, items: ["Derivatives & Options", "IPOs", "Art & Collectibles", "Precious Metals"], w: "40%" },
    { id: "mid", label: "Accumulation", sub: "Growth & Income", color: gold, items: ["Mutual Funds", "Stocks & Bonds", "Real Estate Investments", "RRSPs & TFSAs"], w: "65%" },
    { id: "base", label: "Security", sub: "Build this FIRST", color: green, items: ["Life & Disability Insurance", "Will & Power of Attorney", "Emergency Fund (3–6 months)", "Primary Residence"], w: "100%" },
  ];
  return (
    <div style={{ margin: "24px 0" }}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, marginBottom: 20 }}>
        {layers.map(l => (
          <div key={l.id} onMouseEnter={() => setHovered(l.id)} onMouseLeave={() => setHovered(null)}
            style={{ width: l.w, background: hovered === l.id ? navy4 : navy3, border: `1px solid ${hovered === l.id ? l.color + "66" : border}`, borderRadius: 8, padding: "14px 20px", cursor: "pointer", transition: "all 0.2s", textAlign: "center" }}>
            <div style={{ fontFamily: "Georgia,serif", fontSize: "1rem", color: l.color, marginBottom: 3 }}>{l.label}</div>
            <div style={{ fontSize: "0.72rem", color: muted }}>{l.sub}</div>
          </div>
        ))}
      </div>
      {hovered && (
        <div style={{ background: navy4, border: `1px solid ${layers.find(l => l.id === hovered).color}44`, borderRadius: 8, padding: 20 }}>
          <div style={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: layers.find(l => l.id === hovered).color, marginBottom: 10 }}>
            {layers.find(l => l.id === hovered).label} Layer — Examples
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {layers.find(l => l.id === hovered).items.map((it, i) => (
              <span key={i} style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 6, padding: "6px 14px", fontSize: "0.83rem", color: textCol }}>{it}</span>
            ))}
          </div>
        </div>
      )}
      {!hovered && <div style={{ fontSize: "0.78rem", color: muted, textAlign: "center" }}>↑ Hover over a layer to see examples</div>}
    </div>
  );
}

// Net Worth Calculator
function NetWorthCalc() {
  const [assets, setAssets] = useState({ home: 500000, rrsp: 100000, car: 20000, other: 0 });
  const [liabs, setLiabs] = useState({ mortgage: 300000, loans: 0, credit: 5000, other: 0 });
  const totalAssets = Object.values(assets).reduce((a, b) => a + (parseFloat(b) || 0), 0);
  const totalLiabs = Object.values(liabs).reduce((a, b) => a + (parseFloat(b) || 0), 0);
  const nw = totalAssets - totalLiabs;
  
  const Field = ({ label, value, onChange }) => (
    <div>
      <label style={{ display: "block", fontSize: "0.72rem", fontWeight: 700, color: muted, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>{label}</label>
      <input type="number" value={value} onChange={e => onChange(e.target.value)} style={{ width: "100%", background: navy4, border: `1px solid ${border}`, borderRadius: 6, padding: "10px 12px", color: white, fontFamily: "monospace", fontSize: "0.9rem", outline: "none" }} />
    </div>
  );
  
  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 28, margin: "24px 0" }}>
      <div style={{ fontFamily: "Georgia,serif", fontSize: "1rem", color: white, marginBottom: 6 }}>🧮 Net Worth Calculator</div>
      <div style={{ fontFamily: "monospace", fontSize: "0.82rem", color: gold, background: "rgba(245,166,35,.12)", border: "1px solid rgba(245,166,35,.2)", padding: "6px 12px", borderRadius: 4, display: "inline-block", marginBottom: 20 }}>Net Worth = Total Assets − Total Liabilities</div>
      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <div style={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: green, marginBottom: 12 }}>Assets</div>
          <div className="grid gap-3">
            <Field label="Home / Real Estate ($)" value={assets.home} onChange={v => setAssets({ ...assets, home: v })} />
            <Field label="RRSP / Investments ($)" value={assets.rrsp} onChange={v => setAssets({ ...assets, rrsp: v })} />
            <Field label="Vehicle ($)" value={assets.car} onChange={v => setAssets({ ...assets, car: v })} />
            <Field label="Other Assets ($)" value={assets.other} onChange={v => setAssets({ ...assets, other: v })} />
          </div>
          <div style={{ background: "rgba(34,197,94,.07)", border: "1px solid rgba(34,197,94,.2)", borderRadius: 6, padding: "12px 16px", marginTop: 12 }}>
            <div style={{ fontSize: "0.68rem", color: green, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em" }}>Total Assets</div>
            <div style={{ fontFamily: "monospace", fontSize: "1.4rem", color: green }}>${totalAssets.toLocaleString("en-CA")}</div>
          </div>
        </div>
        <div>
          <div style={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: red, marginBottom: 12 }}>Liabilities</div>
          <div className="grid gap-3">
            <Field label="Mortgage ($)" value={liabs.mortgage} onChange={v => setLiabs({ ...liabs, mortgage: v })} />
            <Field label="Car / Personal Loans ($)" value={liabs.loans} onChange={v => setLiabs({ ...liabs, loans: v })} />
            <Field label="Credit Card Debt ($)" value={liabs.credit} onChange={v => setLiabs({ ...liabs, credit: v })} />
            <Field label="Other Liabilities ($)" value={liabs.other} onChange={v => setLiabs({ ...liabs, other: v })} />
          </div>
          <div style={{ background: "rgba(239,68,68,.07)", border: "1px solid rgba(239,68,68,.2)", borderRadius: 6, padding: "12px 16px", marginTop: 12 }}>
            <div style={{ fontSize: "0.68rem", color: red, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em" }}>Total Liabilities</div>
            <div style={{ fontFamily: "monospace", fontSize: "1.4rem", color: "#f87171" }}>${totalLiabs.toLocaleString("en-CA")}</div>
          </div>
        </div>
      </div>
      <div style={{ background: navy4, border: `1px solid ${nw >= 0 ? "rgba(245,166,35,.3)" : "rgba(239,68,68,.3)"}`, borderRadius: 8, padding: 20, marginTop: 16, textAlign: "center" }}>
        <div style={{ fontSize: "0.68rem", fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", color: gold, marginBottom: 6 }}>Net Worth</div>
        <div style={{ fontFamily: "Georgia,serif", fontSize: "2.2rem", fontWeight: 900, color: nw >= 0 ? gold : "#f87171" }}>${nw.toLocaleString("en-CA")}</div>
        <div style={{ fontSize: "0.82rem", color: muted, marginTop: 4 }}>${totalAssets.toLocaleString()} − ${totalLiabs.toLocaleString()}</div>
      </div>
    </div>
  );
}

// Discretionary Income Calculator
function DiscIncomeCalc() {
  const [income, setIncome] = useState(4000);
  const [rent, setRent] = useState(1500);
  const [food, setFood] = useState(1000);
  const [loans, setLoans] = useState(500);
  const [other, setOther] = useState(0);
  const totalExp = (parseFloat(rent) || 0) + (parseFloat(food) || 0) + (parseFloat(loans) || 0) + (parseFloat(other) || 0);
  const monthly = (parseFloat(income) || 0) - totalExp;
  const annual = monthly * 12;
  
  const Field = ({ label, val, set }) => (
    <div>
      <label style={{ display: "block", fontSize: "0.72rem", fontWeight: 700, color: muted, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 6 }}>{label}</label>
      <input type="number" value={val} onChange={e => set(e.target.value)} style={{ width: "100%", background: navy4, border: `1px solid ${border}`, borderRadius: 6, padding: "10px 12px", color: white, fontFamily: "monospace", fontSize: "0.9rem", outline: "none" }} />
    </div>
  );
  
  return (
    <div style={{ background: navy3, border: `1px solid ${border}`, borderRadius: 8, padding: 28, margin: "24px 0" }}>
      <div style={{ fontFamily: "Georgia,serif", fontSize: "1rem", color: white, marginBottom: 6 }}>🧮 Discretionary Income Calculator</div>
      <div style={{ fontFamily: "monospace", fontSize: "0.82rem", color: gold, background: "rgba(245,166,35,.12)", border: "1px solid rgba(245,166,35,.2)", padding: "6px 12px", borderRadius: 4, display: "inline-block", marginBottom: 20 }}>Discretionary Income = Total Net Income − Total Expenses</div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3 mb-4">
        <Field label="Monthly Net Income (after tax)" val={income} set={setIncome} />
        <Field label="Rent / Housing ($)" val={rent} set={setRent} />
        <Field label="Food & Utilities ($)" val={food} set={setFood} />
        <Field label="Loan Payments ($)" val={loans} set={setLoans} />
        <Field label="Other Expenses ($)" val={other} set={setOther} />
      </div>
      <div className="grid md:grid-cols-3 gap-3">
        <div style={{ background: "rgba(239,68,68,.07)", border: "1px solid rgba(239,68,68,.2)", borderRadius: 6, padding: "14px 16px", textAlign: "center" }}>
          <div style={{ fontSize: "0.65rem", color: "#f87171", fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>Total Expenses</div>
          <div style={{ fontFamily: "monospace", fontSize: "1.2rem", color: "#f87171" }}>${totalExp.toLocaleString()}/mo</div>
        </div>
        <div style={{ background: "rgba(34,197,94,.07)", border: "1px solid rgba(34,197,94,.2)", borderRadius: 6, padding: "14px 16px", textAlign: "center" }}>
          <div style={{ fontSize: "0.65rem", color: green, fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>Monthly Discretionary</div>
          <div style={{ fontFamily: "monospace", fontSize: "1.2rem", color: monthly >= 0 ? green : "#f87171" }}>${monthly.toLocaleString()}/mo</div>
        </div>
        <div style={{ background: "rgba(245,166,35,.1)", border: "1px solid rgba(245,166,35,.25)", borderRadius: 6, padding: "14px 16px", textAlign: "center" }}>
          <div style={{ fontSize: "0.65rem", color: gold, fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>Annual Savings Potential</div>
          <div style={{ fontFamily: "monospace", fontSize: "1.2rem", color: gold }}>${annual.toLocaleString()}/yr</div>
        </div>
      </div>
    </div>
  );
}

// Quiz
const questions = [
  { q: "Which step in the financial planning process involves setting the terms of engagement and compensation?", opts: ["Implementing recommendations", "Establishing the client-advisor relationship", "Collecting data", "Analyzing data"], correct: 1, fb: { y: "Correct! Step 1 — Establishing the relationship — defines the terms of engagement, compensation, and responsibilities before any advice is given.", n: "Not quite. Step 1 (Establishing the relationship) covers terms of engagement and compensation. Collecting and analyzing data are Steps 2 and 3." } },
  { q: "According to the Life-Cycle Hypothesis, which stage is characterized by 'lack of liquidity' due to a mortgage and children?", opts: ["Early Earning Years", "Family Commitment Years", "Mature Earning Years", "Retirement"], correct: 1, fb: { y: "Correct! The Family Commitment Years (ages 25–35) are defined by high expenses (mortgage, children) and a characteristic lack of liquidity — making it hard to save.", n: "Not quite. Family Commitment Years (Stage 2, ages 25–35) feature the mortgage, children, and the defining lack of liquidity." } },
  { q: "A client has high risk tolerance (willingness) but no savings and unstable income (low capacity). What is their Risk Profile?", opts: ["High", "Medium", "Low", "Balanced"], correct: 2, fb: { y: "Correct! The Risk Profile is always the LOWER of tolerance and capacity. High tolerance + low capacity = Low Risk Profile. Capacity constrains what's suitable.", n: "Not quite. Risk Profile = the LOWER of tolerance and capacity. Even if a client is psychologically willing to take risk, limited financial capacity sets the ceiling." } },
  { q: "In the Financial Planning Pyramid, what constitutes the 'base' that must be established first?", opts: ["Mutual Funds and Stocks", "Insurance, Wills, and Emergency Fund", "Tax Shelters", "Commodities and Art"], correct: 1, fb: { y: "Correct! The base of the pyramid is Security: insurance, wills, emergency fund, and primary residence. You cannot build wealth without this foundation.", n: "Not quite. The base is Security — insurance, wills, emergency fund, home. Mutual funds and stocks are in the middle layer. Commodities/art are speculation at the top." } },
  { q: "Which of the following is considered a Liability on a Net Worth Statement?", opts: ["RRSP balance", "Market value of home", "Mortgage balance", "Annual salary"], correct: 2, fb: { y: "Correct! A mortgage is a liability — money owed. The RRSP and home are assets. Annual salary is income, which appears on a cash flow statement, not a net worth statement.", n: "Not quite. The mortgage is a liability. RRSP and home are assets. Annual salary is income — it belongs on a cash flow statement, not a net worth calculation." } },
  { q: "A 25-year-old client needs a house down payment in 2 years. Which product is most suitable?", opts: ["Equity Growth Fund", "Emerging Markets Fund", "Money Market Fund", "10-Year Bond Fund"], correct: 2, fb: { y: "Correct! A 2-year time horizon requires safety and liquidity — a Money Market Fund is the appropriate choice. Equity funds carry too much short-term volatility risk.", n: "Not quite. With a 2-year time horizon, safety and liquidity are paramount. A Money Market Fund is the only suitable choice. Equity and long-term bond funds carry too much risk." } },
  { q: "Why do Mature Earning Years clients (Stage 3) typically shift back toward equity investments?", opts: ["They have no debts", "They are bored with bonds", "They have longer time horizons for retirement and need tax-efficient growth", "They are gambling with surplus income"], correct: 2, fb: { y: "Correct! Stage 3 clients have stabilized expenses, higher income, and a long runway to retirement — making equities appropriate for growth and tax efficiency (capital gains vs. interest income).", n: "Not quite. Mature earners shift to equities because they have higher income, stable expenses, and a long time horizon to retirement — making growth-oriented, tax-efficient investing appropriate." } },
  { q: "Under the Client Focused Reforms (CFRs), how often must KYC information be reviewed at minimum?", opts: ["Only when the client requests it", "Every 5 years", "Every 36 months (3 years)", "Monthly"], correct: 2, fb: { y: "Correct! CFRs require KYC review at least every 36 months (3 years), or immediately when a major life event occurs (marriage, job loss, inheritance, etc.).", n: "Not quite. CFRs require KYC review every 36 months minimum — or sooner if triggered by a major life change like marriage, divorce, or a significant change in financial circumstances." } },
  { q: "Which document would you use to calculate a client's Discretionary Income?", opts: ["Statement of Net Worth", "Family Budget (Cash Flow Statement)", "The Fund Facts document", "The client's Will"], correct: 1, fb: { y: "Correct! Discretionary Income comes from the cash flow statement (budget) — it shows monthly income vs. expenses. Net Worth is a balance sheet snapshot at a point in time.", n: "Not quite. The Cash Flow Statement (budget) shows income and expenses over time, which is used to calculate discretionary income. Net Worth is a point-in-time balance sheet." } },
  { q: "A client refuses to provide income and net worth details. What is the correct course of action?", opts: ["Estimate the numbers based on their job", "Open the account but mark it Non-discretionary", "Refuse to open the account or process the trade", "Ask them to sign a waiver and proceed"], correct: 2, fb: { y: "Correct! Without financial data, you cannot determine suitability — and suitability is a legal obligation. You must refuse. Guessing or proceeding with a waiver is not acceptable.", n: "Not quite. Suitability is a legal requirement that cannot be met without financial data. You must refuse to open the account or execute the trade — guessing or waiving is a regulatory violation." } },
];

function Quiz() {
  const [current, setCurrent] = useState(0);
  const [answered, setAnswered] = useState({});
  const [done, setDone] = useState(false);
  const total = questions.length;
  const score = Object.values(answered).filter(a => a.correct).length;

  function answer(idx) {
    if (answered[current] !== undefined) return;
    setAnswered({ ...answered, [current]: { chosen: idx, correct: idx === questions[current].correct } });
  }
  function next() {
    if (current < total - 1) setCurrent(current + 1);
    else setDone(true);
  }
  function retry() { setCurrent(0); setAnswered({}); setDone(false); }

  const pct = Math.round(score / total * 100);
  const q = questions[current];
  const ans = answered[current];

  return (
    <div style={{ background: "#111827", border: `1px solid ${border}`, borderRadius: 12, padding: 28, marginTop: 24 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 24, flexWrap: "wrap", gap: 12 }}>
        <div style={{ fontFamily: "Georgia,serif", fontSize: "1.2rem", color: white }}>Chapter 4 Quiz</div>
        <div style={{ fontFamily: "monospace", fontSize: "0.78rem", color: muted }}>Question {current + 1} of {total}</div>
      </div>
      <div style={{ height: 4, background: navy4, borderRadius: 2, marginBottom: 24, overflow: "hidden" }}>
        <div style={{ height: "100%", background: `linear-gradient(90deg,${gold},${blue})`, borderRadius: 2, width: `${(current + 1) / total * 100}%`, transition: "width 0.4s ease" }} />
      </div>

      {!done ? (
        <>
          <div style={{ fontFamily: "Georgia,serif", fontSize: "1rem", color: white, lineHeight: 1.55, marginBottom: 20 }}>{q.q}</div>
          <div className="grid gap-3">
            {q.opts.map((opt, i) => {
              const isChosen = ans?.chosen === i;
              const isCorrect = i === q.correct;
              let bg = navy3, borderC = border, col = textCol;
              if (ans !== undefined) {
                if (isCorrect) { bg = "rgba(34,197,94,.08)"; borderC = "#22c55e"; col = "#4ade80"; }
                else if (isChosen) { bg = "rgba(239,68,68,.08)"; borderC = "#ef4444"; col = "#f87171"; }
              }
              return (
                <button key={i} onClick={() => answer(i)} disabled={!!ans}
                  style={{ background: bg, border: `1px solid ${borderC}`, borderRadius: 8, padding: "14px 18px", textAlign: "left", fontFamily: "inherit", fontSize: "0.88rem", color: col, cursor: ans ? "default" : "pointer", transition: "all 0.2s", display: "flex", alignItems: "center", gap: 12 }}>
                  <span style={{ width: 26, height: 26, flexShrink: 0, background: navy4, border: `1px solid ${border}`, borderRadius: 5, display: "inline-flex", alignItems: "center", justifyContent: "center", fontFamily: "monospace", fontSize: "0.72rem", color: muted }}>
                    {["A", "B", "C", "D"][i]}
                  </span>
                  {opt}
                </button>
              );
            })}
          </div>
          {ans && (
            <>
              <div style={{ marginTop: 14, padding: "13px 16px", borderRadius: 7, fontSize: "0.84rem", lineHeight: 1.65, background: ans.correct ? "rgba(34,197,94,.07)" : "rgba(239,68,68,.07)", border: `1px solid ${ans.correct ? "rgba(34,197,94,.25)" : "rgba(239,68,68,.2)"}`, color: ans.correct ? "#86efac" : "#fca5a5" }}>
                {ans.correct ? "✓ " : "✗ "}{ans.correct ? q.fb.y : q.fb.n}
              </div>
              <button onClick={next} style={{ display: "block", marginTop: 20, background: gold, color: "#0b0f1a", border: "none", padding: "12px 28px", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, borderRadius: 100, cursor: "pointer", letterSpacing: "0.04em" }}>
                {current < total - 1 ? "Next Question →" : "See My Results →"}
              </button>
            </>
          )}
        </>
      ) : (
        <div style={{ textAlign: "center", padding: "20px 0" }}>
          <div style={{ width: 110, height: 110, borderRadius: "50%", margin: "0 auto 20px", background: `conic-gradient(${gold} 0% ${pct}%, ${navy4} ${pct}%)`, display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
            <div style={{ position: "absolute", inset: 14, borderRadius: "50%", background: "#111827" }} />
            <span style={{ position: "relative", fontFamily: "Georgia,serif", fontSize: "1.5rem", fontWeight: 900, color: gold, zIndex: 1 }}>{score}/{total}</span>
          </div>
          <div style={{ fontFamily: "Georgia,serif", fontSize: "1.4rem", color: white, marginBottom: 8 }}>
            {score === total ? "Perfect Score! 🏆" : score >= 8 ? "Strong Result ✦" : score >= 6 ? "Good Effort" : "Keep Studying"}
          </div>
          <div style={{ fontSize: "0.9rem", color: muted, marginBottom: 24 }}>
            {score === total ? "Flawless. Complete mastery of Chapter 4." : score >= 8 ? "Excellent work. Review the questions you missed." : score >= 6 ? "Solid attempt. Revisit the life-cycle and KYC sections." : "Go back through the content — focus on the 6 steps, life cycle, and risk profile."}
          </div>
          <button onClick={retry} style={{ background: "transparent", color: gold, border: `1px solid rgba(245,166,35,.35)`, padding: "10px 26px", fontFamily: "inherit", fontSize: "0.85rem", fontWeight: 700, borderRadius: 100, cursor: "pointer" }}>↺ Try Again</button>
        </div>
      )}
    </div>
  );
}

// Glossary
const glossaryTerms = [
  { term: "Financial Planning", def: "A process-driven method to assess a client's current situation and define strategies to achieve future financial goals." },
  { term: "Net Worth", def: "Total Assets minus Total Liabilities. A snapshot of a client's wealth at a specific point in time." },
  { term: "Discretionary Income", def: "Total net income minus total living expenses. Represents the amount available for saving and investing." },
  { term: "Emergency Fund", def: "3–6 months of living expenses held in liquid assets (e.g., Money Market Funds) before aggressive investing begins." },
  { term: "Risk Profile", def: "The lower of risk tolerance and risk capacity. Determines the maximum level of risk that is suitable for a client." },
  { term: "Risk Tolerance", def: "The psychological willingness to accept investment risk — can the client emotionally handle a portfolio decline?" },
  { term: "Risk Capacity", def: "The financial ability to absorb a loss without affecting the ability to meet financial goals." },
  { term: "Safety of Capital", def: "An investment objective focused on preserving the principal amount. Suited to money market or short-term bond funds." },
  { term: "Current Income", def: "An investment objective focused on generating regular cash flow (interest/dividends) rather than growth." },
  { term: "Capital Growth", def: "An investment objective focused on increasing the value of the investment over time. Suited to equity funds." },
  { term: "Life-Cycle Hypothesis", def: "The theory that clients move through predictable financial stages from Early Earning to Retirement, each with different needs." },
  { term: "Family Commitment Stage", def: "Stage 2 (ages 25–35): defined by lack of liquidity due to mortgages, children, and high expenses." },
  { term: "Financial Planning Pyramid", def: "A visual framework: Security (base) → Accumulation (middle) → Speculation (top). Build from the bottom up." },
  { term: "CFR Review Cycle", def: "Under Client Focused Reforms, KYC must be reviewed at a minimum every 36 months, or after major life events." },
  { term: "Time Horizon", def: "When the client will need their money. Short (<3 yrs) = safety. Medium (3–10 yrs) = balance. Long (10+ yrs) = growth." },
  { term: "Suitability", def: "The legal requirement that every recommendation aligns with the client's KYC profile. The foundation of the rep's obligations." },
];

// Main Export
const Chapter4Interactive = () => {
  return (
    <div className="space-y-8">
      {/* Section 1: Overview */}
      <section style={s.section}>
        <div style={s.eyebrow}>
          <span style={s.tag}>Section 01</span>
          <span style={s.badge}>Client Communication</span>
        </div>
        <h2 style={s.h2}>The Importance of Client Communication & Planning</h2>
        <p style={s.prose}>
          Your primary obligation is <span style={{ color: gold, fontWeight: 600 }}>suitability</span> — ensuring every recommendation genuinely fits the client's unique situation. This demands a complete, documented understanding of their goals, financial circumstances, and constraints. The financial planning approach provides a systematic, repeatable framework to accomplish this.
        </p>
        <div className="grid md:grid-cols-2 gap-4 my-6">
          <ColCard label="Ad Hoc Approach" labelColor={muted} accent={muted} title="❌ Without a Plan" body="Reactive recommendations based on what the client asks for. No structure, no documentation. Exposes the advisor to liability and the client to poor outcomes." />
          <ColCard label="Process-Driven" labelColor={gold} accent={gold} title="✓ Financial Planning Approach" body="A structured, documented process that assesses the client's current situation and defines strategies to achieve future goals. Repeatable and defensible." />
        </div>
        <Callout icon="🎯">
          <strong>Goal Setting Must Be Specific:</strong> "I want to be rich" is not a goal. "I need $40,000/year in retirement income starting at age 65, with a 25-year time horizon" is a goal — it is measurable, time-bound, and actionable.
        </Callout>
      </section>

      <hr style={s.hr} />

      {/* Section 2: Six Steps */}
      <section style={s.section}>
        <div style={s.eyebrow}>
          <span style={s.tag}>Section 02</span>
          <span style={s.badge}>Process</span>
        </div>
        <h2 style={s.h2}>The Six Steps of Financial Planning</h2>
        <p style={s.prose}>
          Financial planning is a <span style={{ color: gold, fontWeight: 600 }}>continuous loop</span>, not a one-time event. Each step builds on the last, and the cycle repeats whenever a client's circumstances change. Click each step to learn what it involves:
        </p>
        <SixSteps />
        <Callout icon="🔄" accent={blue}>
          <strong style={{ color: blue2 }}>The Planning Loop:</strong> Step 6 (Periodic Review) feeds directly back into Step 2 (Collect Data). A client's situation is never static — life changes constantly trigger new planning cycles. Under <strong style={{ color: blue2 }}>CFRs</strong>, review is required at minimum every 36 months.
        </Callout>
      </section>

      <hr style={s.hr} />

      {/* Section 3: KYC */}
      <section style={s.section}>
        <div style={s.eyebrow}>
          <span style={s.tag}>Section 03</span>
          <span style={s.badge}>KYC Rule</span>
        </div>
        <h2 style={s.h2}>Collecting & Analyzing Data — The KYC Rule</h2>
        <p style={s.prose}>
          The <span style={{ color: gold, fontWeight: 600 }}>Know Your Client (KYC)</span> rule requires gathering six specific categories of information before making any recommendation. This isn't a checklist — it's the evidentiary foundation of suitability. Click each card to explore:
        </p>
        <KYCAccordion />
        <Callout icon="⚠️" accent={red}>
          <strong style={{ color: "#f87171" }}>Risk Profile = The Lower of Two Measures:</strong> A client who is wealthy (high capacity) but terrified of loss (low tolerance) has a <strong style={{ color: "#f87171" }}>LOW risk profile</strong>. The more conservative of the two measures always governs. You cannot recommend more risk than either dimension allows.
        </Callout>
      </section>

      <hr style={s.hr} />

      {/* Section 4: Life Cycle */}
      <section style={s.section}>
        <div style={s.eyebrow}>
          <span style={s.tag}>Section 04</span>
          <span style={s.badge}>Life-Cycle Hypothesis</span>
        </div>
        <h2 style={s.h2}>The Life-Cycle Hypothesis</h2>
        <p style={s.prose}>
          Clients move through five predictable life stages, each with distinct financial characteristics and appropriate asset allocation strategies. Understanding where a client sits in their life cycle is essential for making suitable recommendations. Click each stage to explore:
        </p>
        <LifeCycle />
        <Callout icon="💡">
          <strong>Why Stage 2 is a Trap:</strong> Family Commitment clients often want aggressive growth (long time horizon) but their actual risk capacity is low — they have a mortgage, dependents, and limited liquid savings. The <strong>lack of liquidity</strong> is the defining feature of this stage, not the time horizon.
        </Callout>
      </section>

      <hr style={s.hr} />

      {/* Section 5: Pyramid */}
      <section style={s.section}>
        <div style={s.eyebrow}>
          <span style={s.tag}>Section 05</span>
          <span style={s.badge}>Financial Planning Pyramid</span>
        </div>
        <h2 style={s.h2}>The Financial Planning Pyramid</h2>
        <p style={s.prose}>
          A visual framework for prioritizing a client's financial needs. The rule is simple: <span style={{ color: gold, fontWeight: 600 }}>you must build a strong foundation before taking risks</span>. Speculation at the top is only appropriate when the base and middle layers are firmly in place. Hover over each layer:
        </p>
        <Pyramid />
        <Callout icon="🏗️" accent={green}>
          <strong style={{ color: green }}>The Foundation Rule:</strong> No client should be investing in mutual funds (middle layer) without first having life insurance, a will, and an emergency fund (base layer). Recommending growth investments to an uninsured client with no emergency fund is an unsuitable recommendation, regardless of their stated goals.
        </Callout>
      </section>

      <hr style={s.hr} />

      {/* Calculators */}
      <section style={s.section}>
        <div style={s.eyebrow}>
          <span style={s.tag}>Interactive Tools</span>
          <span style={s.badge}>Formulas & Practice</span>
        </div>
        <h2 style={s.h2}>Net Worth & Discretionary Income Calculators</h2>
        <p style={s.prose}>
          The two core Chapter 4 calculations — used to assess a client's financial snapshot and savings capacity. Update any field and results recalculate in real time.
        </p>
        <NetWorthCalc />
        <DiscIncomeCalc />
      </section>

      <hr style={s.hr} />

      {/* Quiz */}
      <section style={s.section}>
        <div style={s.eyebrow}>
          <span style={s.tag}>Knowledge Check</span>
          <span style={s.badge}>10 Questions</span>
        </div>
        <h2 style={s.h2}>Test Your Understanding</h2>
        <p style={s.prose}>
          10 questions covering all five sections of Chapter 4. Detailed feedback on every answer.
        </p>
        <Quiz />
      </section>

      <hr style={s.hr} />

      {/* Glossary */}
      <section style={s.section}>
        <div style={s.eyebrow}>
          <span style={s.tag}>Quick Reference</span>
          <span style={s.badge}>Key Terms</span>
        </div>
        <h2 style={s.h2}>Glossary</h2>
        <p style={s.prose}>
          A quick-reference index of all essential terms from Chapter 4.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-6">
          {glossaryTerms.map((g, i) => (
            <div key={i} className="bg-slate-800 border border-slate-700 rounded-lg p-4 hover:border-slate-600 transition-all">
              <div style={{ fontFamily: "monospace", fontSize: "0.72rem", color: gold, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6 }}>{g.term}</div>
              <div style={{ fontSize: "0.83rem", color: muted, lineHeight: 1.65 }}>{g.def}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Chapter4Interactive;
